import { Fragment, type ReactNode } from 'react';
import { MoreVertical } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import { cn } from '@/lib/utils';

/**
 * Shared hover overlay for list rows (emails and sender groups). The action
 * cluster is right-anchored with a FIXED slot layout so a given action sits at
 * the same x-position on every row the cursor passes over:
 *
 *   [leading (variable width)] | archive delete mark | <middle slots> | more | date
 *
 * Row-specific, sometimes-present content (label chips, approve/block) goes in
 * `leading` — left of the fixed cluster, where its width can vary without
 * shifting the core buttons. `middle` always renders exactly its declared
 * slots; a null slot becomes an invisible spacer so email rows (reply /
 * reply-all / forward) and sender-group rows (open latest) stay aligned when
 * both types appear in one grouped list.
 */

export interface HoverActionConfig {
  key: string;
  icon: ReactNode;
  label: string;
  onAction: () => void;
  disabled?: boolean;
  tone?: 'primary' | 'destructive' | 'success' | 'muted';
}

const TONE_CLASSES: Record<NonNullable<HoverActionConfig['tone']>, string> = {
  primary: 'hover:bg-primary/10 hover:text-primary',
  destructive: 'hover:bg-destructive/10 hover:text-destructive',
  success: 'hover:bg-success/10 hover:text-success-ink',
  muted: 'hover:bg-muted/50 hover:text-foreground',
};

export function HoverActionButton({ action }: { action: HoverActionConfig }) {
  return (
    <Button
      type="button"
      variant="ghost"
      size="icon"
      className={cn(
        'pointer-events-auto h-8 w-8 rounded-full transition-colors',
        TONE_CLASSES[action.tone ?? 'primary'],
      )}
      disabled={action.disabled}
      onClick={(event) => {
        event.stopPropagation();
        action.onAction();
      }}
      onMouseDown={(event) => {
        event.stopPropagation();
      }}
      title={action.label}
      aria-label={action.label}
    >
      {action.icon}
    </Button>
  );
}

function HoverDivider() {
  return <div className="h-4 w-px bg-border/60 mx-1" />;
}

interface RowHoverActionsProps {
  /** Variable-width, row-specific content at the left edge of the overlay. */
  leading?: ReactNode;
  /** Fixed core: archive, delete, mark read/unread — identical on every row. */
  primary: HoverActionConfig[];
  /** Fixed slots after the core; null renders an invisible spacer. */
  middle?: Array<HoverActionConfig | null>;
  /** Content for the More menu; the trigger renders whenever provided. */
  overflow?: ReactNode;
  /** Extra indicators (e.g. tracking) rendered between More and the date. */
  trailing?: ReactNode;
  dateShort: string;
  dateFull: string;
}

export function RowHoverActions({
  leading,
  primary,
  middle,
  overflow,
  trailing,
  dateShort,
  dateFull,
}: RowHoverActionsProps) {
  const middleSlots = middle ?? [];

  return (
    // Mouse/trackpad only (hover-device): on a touch screen a tap focuses the
    // row, and a focus-within reveal threw this overlay over the row before
    // the click landed — the tap hit the overlay instead of opening the row.
    // Keyboard focus still reveals it via :focus-visible (never set by a tap).
    <div className="absolute inset-y-0 right-0 hidden md:hover-device:flex items-stretch rounded-r-xl overflow-hidden">
      <div className="pointer-events-none flex h-full items-center">
        <div className="pl-24 pr-2 pointer-events-none h-full flex items-center gap-1 bg-gradient-to-l from-card from-80% to-transparent text-muted-foreground transition-all duration-200 ease-out z-20 opacity-0 translate-x-full group-hover:opacity-100 group-hover:translate-x-0 group-has-[:focus-visible]:opacity-100 group-has-[:focus-visible]:translate-x-0 group-hover:pointer-events-auto group-has-[:focus-visible]:pointer-events-auto">
          {leading}
          {leading != null && <HoverDivider />}
          {primary.map((action) => (
            <HoverActionButton key={action.key} action={action} />
          ))}
          {middleSlots.length > 0 && <HoverDivider />}
          {middleSlots.map((action, index) =>
            action ? (
              <HoverActionButton key={action.key} action={action} />
            ) : (
              <span key={`slot-${index}`} aria-hidden className="h-8 w-8 flex-shrink-0" />
            ),
          )}
          {overflow != null && (
            <Fragment>
              <HoverDivider />
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    className={cn(
                      'pointer-events-auto h-8 w-8 rounded-full transition-colors',
                      TONE_CLASSES.muted,
                    )}
                    onClick={(event) => {
                      event.stopPropagation();
                    }}
                    onMouseDown={(event) => {
                      event.stopPropagation();
                    }}
                    title="More actions"
                    aria-label="More actions"
                  >
                    <MoreVertical className="h-4 w-4" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-60">
                  {overflow}
                </DropdownMenuContent>
              </DropdownMenu>
            </Fragment>
          )}
          <HoverDivider />
          {trailing}
          <Tooltip>
            <TooltipTrigger asChild>
              <span className="text-xs text-muted-foreground min-w-[68px] text-right font-medium tabular-nums">
                {dateShort}
              </span>
            </TooltipTrigger>
            <TooltipContent side="top" align="end">
              {dateFull}
            </TooltipContent>
          </Tooltip>
        </div>
      </div>
    </div>
  );
}
